import { Compass, LogOut, User as UserIcon } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { ROLE_LABELS } from '@cfl/assets-shared';

import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  SidebarTrigger,
} from '@/components/cargofl-ui';
import { AppearanceMenu } from '@/components/shell/appearance-menu';
import { pageMeta } from '@/lib/page-meta';
import { useCurrentUser, useSignOut } from '@/lib/session';

function initials(label: string): string {
  const parts = label.trim().split(/[\s@.]+/).filter(Boolean);
  return (parts[0]?.[0] ?? '?').concat(parts[1]?.[0] ?? '').toUpperCase();
}

export function TopBar({
  actions,
  onStartTour,
}: {
  actions?: React.ReactNode;
  /** Absent outside the app shell, which is why the menu item is conditional. */
  onStartTour?: () => void;
}) {
  const { pathname } = useLocation();
  const user = useCurrentUser();
  const signOut = useSignOut();
  const { title, subtitle } = pageMeta(pathname);

  return (
    <header className="sticky top-0 z-30 flex h-[60px] items-center gap-4 border-b bg-background px-4 md:px-6">
      <SidebarTrigger />

      <div className="min-w-0 flex-1">
        <h1 className="truncate text-base font-bold tracking-[-0.01em]">{title}</h1>
        {subtitle ? <p className="truncate text-xs text-muted-foreground">{subtitle}</p> : null}
      </div>

      {/* Pages inject their own primary action here, so there is one CTA slot in the
          app rather than a differently-placed button on every page header. */}
      {actions}

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button hint={"Your profile and sign out"} variant="ghost" className="flex items-center gap-2 px-2">
            <Avatar className="size-7">
              {user.avatarUrl ? <AvatarImage src={user.avatarUrl} alt={user.name} /> : null}
              <AvatarFallback className="text-xs">{initials(user.name)}</AvatarFallback>
            </Avatar>
            <span className="hidden text-sm font-medium md:inline">{user.name}</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-64">
          <DropdownMenuLabel>
            <div className="flex flex-col">
              <span className="truncate font-medium">{user.name}</span>
              <span className="truncate text-xs font-normal text-muted-foreground">
                {user.email ?? 'No email on record'}
              </span>
              {/* Employee code as well as role: it is the identifier every bulk
                  assignment file matches on, so the person using those files needs to
                  be able to read their own without going to another screen. */}
              <span className="mt-1 truncate text-xs font-normal text-muted-foreground">
                {user.employeeCode} · {ROLE_LABELS[user.role]}
                {user.department ? ` · ${user.department}` : ''}
              </span>
            </div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem asChild>
            <Link to="/my-assets">
              <UserIcon className="mr-2 size-4" />
              My assets
              {user.assetsHeld > 0 ? (
                <span className="ml-auto text-xs tabular-nums text-muted-foreground">
                  {user.assetsHeld}
                </span>
              ) : null}
            </Link>
          </DropdownMenuItem>
          {onStartTour && (
            <DropdownMenuItem onSelect={onStartTour}>
              <Compass className="mr-2 size-4" />
              Take the tour
            </DropdownMenuItem>
          )}
          <DropdownMenuSeparator />
          {/* Theme moved in here from a standalone header button - it now sits
              with the accent colour and font, which are the same kind of choice. */}
          <AppearanceMenu />
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={() => void signOut()}>
            <LogOut className="mr-2 size-4" />
            Sign out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </header>
  );
}
